import { useSelector } from "react-redux";
import { AppState } from "../../store";
import { currentSiteGroupsSelector, selectedGeoRegionIdsSelector, selectedGeoRegionSelector } from "../../store/api/admin/selectors";
import { IGeoRegion } from "../../store/api/admin/types";
import { IDevicesToSiteGroup, ISiteGroup } from "../../store/api/devices/types";
import { useEffect, useMemo, useRef, useState } from "react";
import { useRegionDisplay } from "../../pages/Administrations/AccessControls/Regions/hooks";
import { useStoreDispatch } from "../../store/hooks";
import { basicDialogSelector, deviceCountSelector, selectedNavListItemsSelector } from "../../store/helpers/helperSelectors";
import DevicesIcon from "@mui/icons-material/Devices";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import ListIcon from "@mui/icons-material/List";
import SyncProblemIcon from "@mui/icons-material/SyncProblem";
import BuildIcon from "@mui/icons-material/Build";
import PowerSettingsNewIcon from "@mui/icons-material/PowerSettingsNew";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import { setBasicDialogOpenFor, setConfirmDialog, setOpenToast, setSelectedNavListItems } from "../../store/helpers/helperReducers";
import { DIALOGOPENFOR, LOCAL_STORAGE } from "../../globals/globals";
import { initialGeoRegion } from "../helpers";
import {
  useAddDevicesToSiteGroupMutation,
  useCreateSiteGroupMutation,
  useDeleteSiteGroupMutation,
  useRemoveDevicesFromSiteGroupMutation,
  useGetNodesCountsQuery,
} from "../../store/api/devices/deviceApiSlice";
import { errorMessage, titleCase } from "../../utils/helpers";
import { noOfSelectedRowSelector } from "../../store/api/devices/selectors";
import { setNoOfSelectedRow } from "../../store/api/devices/reducers";
import { constStr } from "../../globals/constants";
import { setCurrentSiteGroups, setSelectedGeoRegion } from "../../store/api/admin/reducers";
import { selectProfilePermissionsSelector } from "../../store/api/auth/selectors";
import ProfilePermissions from "../../utils/ProfilePermissions";
import { useLocation } from "react-router-dom";
import { setDeviceCount } from "../../store/helpers/helperReducers";
import { API } from "../../globals/globals";

export const useNodeLeftSiteNav = () => {
  const initialSiteGroup: ISiteGroup = useMemo(() => {
    return {
      parentKey: "",
      objectKey: "",
      name: "",
      icon: undefined,
      deviceCount: 0,
    };
  }, []);

  const dispatch = useStoreDispatch();
  const location = useLocation();
  const selectedGeoRegion = useSelector<AppState, IGeoRegion>(selectedGeoRegionSelector);
  const selectedGeoRegionIds = useSelector(selectedGeoRegionIdsSelector);
  const { deviceRowIds } = useSelector(noOfSelectedRowSelector);
  const basicDialogOpenFor = useSelector(basicDialogSelector);
  const currentSiteGroups = useSelector(currentSiteGroupsSelector);
  const permissions: ProfilePermissions | null = useSelector(selectProfilePermissionsSelector);

  const [selectedSiteGroup, setSelectedSiteGroup] = useState<ISiteGroup>();
  const [devicesToSiteGroupData, setDevicesToSiteGroupData] = useState<IDevicesToSiteGroup>();
  const [siteGroupFormData, setSiteGroupFormData] = useState<ISiteGroup>(initialSiteGroup);
  const isEnableSave = !!siteGroupFormData?.name;

  const { geoRegions, refetchGeoRegions } = useRegionDisplay();
  const [createSiteGroup] = useCreateSiteGroupMutation();
  const [deleteSiteGroup] = useDeleteSiteGroupMutation();
  const [addDevicesToSiteGroup] = useAddDevicesToSiteGroupMutation();
  const [removeDevicesFromSiteGroup] = useRemoveDevicesFromSiteGroupMutation();
  const [currentRoute, setCurrentRoute] = useState(localStorage.getItem('currentRoute') || "");
  const [previousRoute, setPreviousRoute] = useState(localStorage.getItem('previousRoute') || "");
  const { nodeItemIndex } = useSelector(selectedNavListItemsSelector);

  const countsRegionIds = useMemo(() => {
    if (selectedGeoRegionIds && selectedGeoRegionIds.length > 0) {
      return selectedGeoRegionIds;
    }

    if (selectedGeoRegion?.objectKey) {
      return [selectedGeoRegion.objectKey];
    }

    return [];
  }, [selectedGeoRegion?.objectKey, selectedGeoRegionIds]);

  const { data: nodesCounts } = useGetNodesCountsQuery(
    { geoRegionIds: countsRegionIds },
    { pollingInterval: API.ENDPOINTS.NODESCOUNTS.pollingInterval || API.POLLING_INTERVAL }
  );

  useEffect(() => {
    if (geoRegions && geoRegions.length && geoRegions[0] != null) {
      const updatedGeoRegions = geoRegions ? geoRegions : [];

      // Always show all regions and their site groups in the navigation tree
      // The map view filtering is handled separately via selectedGeoRegionIds in the map API calls
      const aggregatedSiteGroups: ISiteGroup[] = [];
      updatedGeoRegions.forEach((region: IGeoRegion) => {
        if (region && region.siteGroups && region.siteGroups.length) {
          region.siteGroups.forEach((sg) => {
            if (!aggregatedSiteGroups.find((existing) => existing.objectKey === sg.objectKey)) {
              aggregatedSiteGroups.push(sg);
            }
          });
        }
      });

      dispatch(setCurrentSiteGroups(aggregatedSiteGroups));

      // Preserve CURRENTREGION for legacy uses (same as before, using single selectedGeoRegion)
      const currentRegionSiteGroups = updatedGeoRegions.find((region: IGeoRegion) => region.objectKey === selectedGeoRegion.objectKey)?.siteGroups || [];
      !!selectedGeoRegion.objectKey && localStorage.setItem(LOCAL_STORAGE.CURRENTREGION, JSON.stringify({ ...selectedGeoRegion, siteGroups: currentRegionSiteGroups }));
    }
  }, [selectedGeoRegion, selectedGeoRegionIds, geoRegions, initialSiteGroup, dispatch]);

  useEffect(() => {
    if (previousRoute !== '/nodes' && currentRoute === '/nodes') {
      const navItem = typeof location.state === "string" ? location.state : (nodeItemIndex ?? "devices");
      dispatch(setSelectedNavListItems(navItem));
    }

    localStorage.setItem('previousRoute', currentRoute);
    localStorage.setItem('currentRoute', window.location.pathname);

    setPreviousRoute(currentRoute);
    setCurrentRoute(window.location.pathname);
  }, [currentRoute, previousRoute]);

  useEffect(() => {
    if (!nodesCounts?.totals) {
      return;
    }

    const {
      allDeviceCount = 0,
      disconnectedDeviceCount = 0,
      inMaintenanceModeCount = 0,
      ungroupedCount = 0,
      kpiSyncErrorCount = 0,
      offLineDeviceCount = 0,
      configMismatchCount = 0,
    } = nodesCounts.totals;

    localStorage.setItem("allDevice", allDeviceCount.toString());
    localStorage.setItem("disconnected", disconnectedDeviceCount.toString());
    localStorage.setItem("inMaintenanceMode", inMaintenanceModeCount.toString());
    localStorage.setItem("ungrouped", ungroupedCount.toString());
    localStorage.setItem("kpiErorrCount", kpiSyncErrorCount.toString());
    localStorage.setItem("offLineDeviceCount", offLineDeviceCount.toString());
    localStorage.setItem("configMismatchCount", configMismatchCount.toString());

    dispatch(setDeviceCount({
      disconnectedDeviceCount,
      allDeviceCount,
      inMaintenanceModeCount,
      ungroupedCount,
      kpiSyncErrorCount,
      offLineDeviceCount,
      configMismatchCount,
    }));
  }, [dispatch, nodesCounts]);

  const {
    disconnectedDeviceCount,
    allDeviceCount,
    ungroupedCount,
    inMaintenanceModeCount,
    kpiSyncErrorCount,
    offLineDeviceCount,
    configMismatchCount,
  } = useSelector(deviceCountSelector);
  const ungroupDeviceList = [
    {
      ...initialSiteGroup,
      objectKey: constStr.ungrouped,
      name: "Ungrouped",
      deviceCount: ungroupedCount,
      icon: ListIcon,
      countColor: "--link",
    },
  ];

  const filterGroupList = [
    {
      ...initialSiteGroup,
      objectKey: "devices",
      name: "All Devices",
      deviceCount: allDeviceCount,
      icon: DevicesIcon,
      countColor: "--link",
    },
    {
      ...initialSiteGroup,
      objectKey: "disconnected",
      name: "Disconnected",
      deviceCount: disconnectedDeviceCount,
      icon: LinkOffIcon,
      countColor: "--error-dark",
    },
    {
      ...initialSiteGroup,
      objectKey: "pmCollected",
      name: "KPI Sync Errors",
      deviceCount: kpiSyncErrorCount,
      icon: SyncProblemIcon,
      countColor: "--error-dark",
    },
    {
      ...initialSiteGroup,
      objectKey: "maintenancemode",
      name: "In Maintenance",
      deviceCount: inMaintenanceModeCount,
      icon: BuildIcon,
      countColor: "--warning-light",
    },
    {
      ...initialSiteGroup,
      objectKey: "offline",
      name: "Offline",
      deviceCount: offLineDeviceCount,
      icon: PowerSettingsNewIcon,
      countColor: "--link",
    },
    {
      ...initialSiteGroup,
      objectKey: "configMismatch",
      name: "Config Mismatch",
      deviceCount: configMismatchCount,
      icon: CompareArrowsIcon,
      countColor: "--warning-light",
    },
  ];

  const submitHandler = async () => {
    try {
      const siteGroups = geoRegions.find((region) => region.objectKey === selectedGeoRegion.objectKey)?.siteGroups;
      const nameExists = siteGroups?.some((siteGroup) => siteGroup.name === siteGroupFormData.name);
      if (nameExists) {
        dispatch(setOpenToast({ openToast: true, toastType: "error", message: `Site Group: ${siteGroupFormData.name} already exists` }));
        return;
      }

      dispatch(setBasicDialogOpenFor(undefined));
      siteGroupFormData.parentKey = selectedGeoRegion.objectKey || initialGeoRegion.objectKey;

      const result: any = await createSiteGroup(siteGroupFormData);

      if (result.error || !result.data) {
        throw result.error.data || "Unknown error";
      }

      dispatch(
        setOpenToast({
          openToast: true,
          toastType: "success",
          message: `Site Group : ${siteGroupFormData.name} created successfully`,
        })
      );
    } catch (error: any) {
      dispatch(
        setOpenToast({
          openToast: true,
          toastType: "error",
          message: errorMessage(error),
        })
      );
    }

    refetchGeoRegions();
  };

  const addSiteGroupHandler = () => {
    if (siteGroupFormData?.name) {
      setSiteGroupFormData({ ...initialSiteGroup });
    }
    dispatch(setBasicDialogOpenFor(DIALOGOPENFOR.SITEGROUPS));
  };

  const openAddSiteGroupDialogForRegion = (regionId: string) => {
    if (geoRegions && geoRegions.length) {
      const region = geoRegions.find((rg) => rg.objectKey === regionId);
      if (region) {
        dispatch(setSelectedGeoRegion(region));
      }
    }
    addSiteGroupHandler();
  };

  const handleOnChange = (event: any) => {
    event.preventDefault();
    const { name, value } = event.target;
    setSiteGroupFormData({ ...siteGroupFormData, [name]: value });
  };

  const handleMoveDeviceToSiteGroup = (event: any) => {
    event.preventDefault();
    const { value } = event.target;
    const groupkey = value || "";
    setDevicesToSiteGroupData({ groupkey, deivceKeys: deviceRowIds });
  };

  const deviceToSiteGroupHandler = async () => {
    try {
      dispatch(setBasicDialogOpenFor(undefined));
      const siteGroup = currentSiteGroups?.find((group: ISiteGroup) => group.objectKey === devicesToSiteGroupData?.groupkey);
      const name = siteGroup ? siteGroup.name : "";

      const result: any = await addDevicesToSiteGroup(devicesToSiteGroupData!);

      if (result.error || !result.data) throw result.error;

      dispatch(setNoOfSelectedRow({ deviceRowIds: [] }));
      dispatch(
        setOpenToast({
          openToast: true,
          toastType: "success",
          message: `Device/s added to Site Group: ${name} successfully`,
        })
      );
    } catch (error: any) {
      dispatch(
        setOpenToast({
          openToast: true,
          toastType: "error",
          message: errorMessage(error),
        })
      );
    }
  };

  const removeDeviceFromSiteGroupHandler = async () => {
    try {
      dispatch(setBasicDialogOpenFor(undefined));
      const siteGroup = currentSiteGroups?.find((group: ISiteGroup) => group.objectKey === devicesToSiteGroupData?.groupkey);
      const name = siteGroup ? siteGroup.name : "";
      const result: any = await removeDevicesFromSiteGroup(devicesToSiteGroupData!);

      if (result.error || !result.data) {
        throw result.error.data || "Unknown error";
      }

      setSelectedSiteGroup(result.data);

      dispatch(
        setOpenToast({
          openToast: true,
          toastType: "success",
          message: `Device/s removed from Site Group: ${name!} successfully`,
        })
      );
    } catch (error: any) {
      dispatch(
        setOpenToast({
          openToast: true,
          toastType: "error",
          message: errorMessage(error),
        })
      );
    }
  };

  const deleteHandler = (siteGroup?: ISiteGroup) => {
    if (siteGroup) {
      setSelectedSiteGroup(siteGroup);
    }
    dispatch(setConfirmDialog(DIALOGOPENFOR.SITEGROUPS));
  };

  const handleDeleteAction = async (actionName: string) => {
    if (!actionName) return;

    try {
      if (actionName.match("delete")) {
        const result: any = await deleteSiteGroup(selectedSiteGroup?.objectKey);

        if (result.error || !result.data) {
          throw result.error.data || "Unknown error";
        }

        dispatch(
          setOpenToast({
            openToast: true,
            toastType: "success",
            message: `Site Group: ${titleCase(selectedSiteGroup?.name!)} has been deleted`,
          })
        );
        if (selectedGeoRegion && selectedGeoRegion.siteGroups) {
          setSelectedSiteGroup(
            selectedGeoRegion.siteGroups.find((group) => group.objectKey !== selectedSiteGroup?.objectKey) || {
              ...initialGeoRegion,
              parentKey: selectedGeoRegion.objectKey,
            }
          );
        }
      }
    } catch (error: any) {
      dispatch(
        setOpenToast({
          openToast: true,
          toastType: "error",
          message: errorMessage(error),
        })
      );
    }

    refetchGeoRegions();
  };

  return {
    permissions,
    dispatch,
    geoRegions,
    nodesCounts,
    deviceRowIds,
    refetchGeoRegions,
    selectedGeoRegion,
    currentSiteGroups,
    filterGroupList,
    isEnableSave,
    basicDialogOpenFor,
    siteGroupFormData,
    setSiteGroupFormData,
    addSiteGroupHandler,
    openAddSiteGroupDialogForRegion,
    handleOnChange,
    handleMoveDeviceToSiteGroup,
    submitHandler,
    addDevicesToSiteGroup,
    removeDevicesFromSiteGroup,
    deleteSiteGroup,
    deleteHandler,
    handleDeleteAction,
    setSelectedSiteGroup,
    selectedSiteGroup,
    deviceToSiteGroupHandler,
    devicesToSiteGroupData,
    setDevicesToSiteGroupData,
    removeDeviceFromSiteGroupHandler,
    ungroupDeviceList,
  };
};
